import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyFormOptions } from '@/mocks/applyData';
import { localizedPath } from '@/lib/contactApi';

interface FormData {
  name: string;
  furigana: string;
  email: string;
  phone: string;
  desiredCourse: string;
  preferredTerm: string;
  transferDiscount: string;
  motivation: string;
  consultationDesired: string;
  privacyAgreed: boolean;
}

const initialFormData: FormData = {
  name: '',
  furigana: '',
  email: '',
  phone: '',
  desiredCourse: '',
  preferredTerm: '',
  transferDiscount: 'none',
  motivation: '',
  consultationDesired: '',
  privacyAgreed: false,
};

export default function ApplicationForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('apply.form.error.name');
    if (!formData.furigana.trim()) newErrors.furigana = t('apply.form.error.furigana');
    if (!formData.email.trim()) {
      newErrors.email = t('apply.form.error.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('apply.form.error.emailInvalid');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('apply.form.error.phone');
    }
    if (!formData.desiredCourse) newErrors.desiredCourse = t('apply.form.error.course');
    if (!formData.consultationDesired) newErrors.consultationDesired = t('apply.form.error.consultation');
    if (formData.motivation.length > 500) {
      newErrors.motivation = t('apply.form.error.motivation');
    }
    if (!formData.privacyAgreed) {
      newErrors.privacyAgreed = t('apply.form.error.privacy');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      navigate(localizedPath('/apply/confirm'), { state: { formData } });
    }
  };

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/40" id="apply-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('apply.form.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('apply.form.title')}
          </h2>
          <p className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">
            {t('apply.form.titleEn')}
          </p>
          <div className="gold-line mx-auto mb-5"></div>
          <p className="text-[15px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            <span className="text-accent-600 text-[12px] mr-1">{t('apply.form.requiredBadge')}</span>
            {t('apply.form.requiredNote')}
          </p>
        </div>

        {/* Alternative contact */}
        <div className="text-center mb-10 bg-background-50 border border-background-200/60 p-5">
          <p className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            {t('apply.form.altContact')}{' '}
            <a href="tel:0368244086" className="text-foreground-800 font-medium hover:text-accent-600 transition-colors">
              03-6824-4086
            </a>
            {t('apply.form.altContactHours')}{' '}
            <a href="mailto:info@butler-academy.jp" className="text-accent-600 hover:text-accent-500 transition-colors">
              info@butler-academy.jp
            </a>
            {t('apply.form.altContactSuffix')}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-7">
            {/* Name */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.nameLabel')} <span className="text-accent-600 text-[12px]">{t('apply.form.requiredBadge')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('apply.form.namePlaceholder')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            {/* Furigana */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.furiganaLabel')} <span className="text-accent-600 text-[12px]">{t('apply.form.requiredBadge')}</span>
              </label>
              <input
                type="text"
                name="furigana"
                value={formData.furigana}
                onChange={(e) => updateField('furigana', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('apply.form.furiganaPlaceholder')}
              />
              {errors.furigana && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.furigana}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.emailLabel')} <span className="text-accent-600 text-[12px]">{t('apply.form.requiredBadge')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('apply.form.emailPlaceholder')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.phoneLabel')} <span className="text-accent-600 text-[12px]">{t('apply.form.requiredBadge')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('apply.form.phonePlaceholder')}
              />
              {errors.phone && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.phone}</p>
              )}
            </div>

            {/* Desired Course */}
            <div>
              <p className="text-[14px] text-foreground-700 tracking-[0.06em] mb-3 font-label">
                {t('apply.form.courseLabel')} <span className="text-accent-600 text-[12px]">{t('apply.form.requiredBadge')}</span>
              </p>
              <div className="space-y-2">
                {applyFormOptions.courses.map((course) => (
                  <label
                    key={course.value}
                    className="flex items-center gap-3 p-3.5 bg-background-50 border border-background-200/60 hover:border-accent-500/30 cursor-pointer transition-colors duration-200"
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                          formData.desiredCourse === course.value
                            ? 'border-accent-500'
                            : 'border-background-300'
                        }`}
                      >
                        {formData.desiredCourse === course.value && (
                          <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                        )}
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="desiredCourse"
                      value={course.value}
                      checked={formData.desiredCourse === course.value}
                      onChange={(e) => updateField('desiredCourse', e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-[16px] text-foreground-800 tracking-[0.04em]">{t(`apply.form.course.${course.value}`)}</span>
                  </label>
                ))}
              </div>
              {errors.desiredCourse && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.desiredCourse}</p>
              )}
            </div>

            {/* Preferred Term */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.termLabel')}
              </label>
              <select
                name="preferredTerm"
                value={formData.preferredTerm}
                onChange={(e) => updateField('preferredTerm', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                <option value="">{t('apply.form.termPlaceholder')}</option>
                {applyFormOptions.terms.map((term) => (
                  <option key={term.value} value={term.value}>{t(`apply.form.term.${term.value}`)}</option>
                ))}
              </select>
            </div>

            {/* Transfer Discount */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.transferLabel')}
              </label>
              <select
                name="transferDiscount"
                value={formData.transferDiscount}
                onChange={(e) => updateField('transferDiscount', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 cursor-pointer appearance-none"
              >
                {applyFormOptions.transferOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(`apply.form.transfer.${opt.value}`)}</option>
                ))}
              </select>
              <p className="text-[13px] text-foreground-400 mt-1.5 tracking-[0.03em]">
                {t('apply.form.transferNote')}
                <Link to="/courses" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors mx-1">
                  {t('apply.form.transferNoteLink')}
                </Link>
                {t('apply.form.transferNoteSuffix')}
              </p>
            </div>

            {/* Motivation */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('apply.form.motivationLabel')} <span className="text-foreground-400 text-[12px]">{t('apply.form.optionalBadge')}</span>
              </label>
              <textarea
                name="motivation"
                value={formData.motivation}
                onChange={(e) => updateField('motivation', e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400 resize-none"
                placeholder={t('apply.form.motivationPlaceholder')}
              ></textarea>
              <p className="text-[13px] text-foreground-400 mt-1 text-right">
                {formData.motivation.length}/500
              </p>
              {errors.motivation && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.motivation}</p>
              )}
            </div>

            {/* Consultation Desired */}
            <div>
              <p className="text-[14px] text-foreground-700 tracking-[0.06em] mb-3 font-label">
                {t('apply.form.consultLabel')} <span className="text-accent-600 text-[12px]">{t('apply.form.requiredBadge')}</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {[
                  { value: 'yes', labelKey: 'apply.form.consultYes' },
                  { value: 'no', labelKey: 'apply.form.consultNo' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-3 p-3.5 bg-background-50 border border-background-200/60 hover:border-accent-500/30 cursor-pointer transition-colors duration-200 flex-1"
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                          formData.consultationDesired === opt.value
                            ? 'border-accent-500'
                            : 'border-background-300'
                        }`}
                      >
                        {formData.consultationDesired === opt.value && (
                          <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                        )}
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="consultationDesired"
                      value={opt.value}
                      checked={formData.consultationDesired === opt.value}
                      onChange={(e) => updateField('consultationDesired', e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-[16px] text-foreground-800 tracking-[0.04em]">{t(opt.labelKey)}</span>
                  </label>
                ))}
              </div>
              {errors.consultationDesired && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.consultationDesired}</p>
              )}
            </div>

            {/* Privacy Policy */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="privacyAgreed"
                  checked={formData.privacyAgreed}
                  onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-colors duration-200 ${
                    formData.privacyAgreed
                      ? 'bg-accent-500 border-accent-500'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {formData.privacyAgreed && (
                    <i className="ri-check-line text-[12px] text-primary-900"></i>
                  )}
                </div>
                <span className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
                  <Link to="/privacy" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors">
                    {t('apply.form.privacyLink')}
                  </Link>
                  {t('apply.form.privacyLabel')}
                  <span className="text-accent-600 text-[12px] ml-1">{t('apply.form.requiredBadge')}</span>
                </span>
              </label>
              {errors.privacyAgreed && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.privacyAgreed}</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('apply.form.submitBtn')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
